/**
 * Live data layer — fetches real org data from the NestJS API's anonymous
 * endpoints (global CORS) and adapts it to the shapes the components render.
 * Everything degrades gracefully: callers fall back to mock data on error or
 * empty results, so a misconfigured org (or the offline playground) still
 * renders something rather than breaking.
 */
import type {
  AppointmentPaymentOptionsDto,
  AppointmentRegistrationContextDto,
  AppointmentTypeGroupDto,
  EmbedMemberContextDto,
  EmbedProductDto,
  EmbedRoomAvailabilityDto,
  EventActivityDto,
  EventDto,
  EventFilterDataDto,
  EventGroupingType,
  EventRegistrationContextDto,
  EventRegistrationPaymentOptionsDto,
  EventRegistrationPaymentPunchCardOptionDto,
  FormDto,
  MonthIndicatorsResponseDto,
  OrgForSessionDto,
  PublicBookingScheduleResponseDto,
  ScheduleTimeslotDto,
  SpotAvailabilityDto,
  ValidatePromotionCodeRequestDto,
  ValidatePromotionCodeResponseDto,
} from "@zipper/embedded-api-client";
import { authHeaders } from "../config/client";
import type {
  MockClass,
  CapacityState,
} from "../components/data/mock-schedule";
import {
  getSafeExternalTicketUrl,
  type MockEvent,
} from "../components/data/mock-events";
import type { PricingTier } from "../components/data/mock-pricing";
import type { MockProduct } from "../components/data/mock-shop";
import type { MemberCreditOption } from "../checkout/member-credits";
import { ownedAccess, type OwnedAccess } from "../checkout/owned-access";
import { getEmbeddedApiClients } from "./embedded-api";
import { discoverCommercePrograms } from "./commerce-gateway";

// ── Component-facing projections over generated OpenAPI models ──

type EmbeddedEventDto = EventDto;

/** Discriminator on the event-grouping an event hangs off of
 * (`EventGroupingType` in apps/api/src/event-grouping/dtos/program.dto.ts).
 * Recurring classes and programs are BOTH stored as event-groupings, so this
 * string is the only thing that tells the two apart. */
export type ApiEventGroupingType = EventGroupingType;

type ApiPaymentOption = Pick<
  NonNullable<EventRegistrationPaymentOptionsDto["payment"]>,
  "productId" | "price"
> &
  Partial<
    Pick<
      NonNullable<EventRegistrationPaymentOptionsDto["payment"]>,
      "ownsProduct" | "isWithinBillingPeriod" | "priceBeforeDiscount" | "deferredChargeSettings" | "deferredMembershipPurchaseId" | "isDeferredMembershipAccess"
    >
  >;
type GeneratedPunchCard = NonNullable<
  EventRegistrationPaymentPunchCardOptionDto["punchCard"]
>;
type CompatiblePunchCard = {
  id?: string;
  /** Compatibility fallback from older/thinner payment-option responses. */
  name?: string;
  price?: number;
  product?: Partial<GeneratedPunchCard["product"]>;
  group?: Partial<GeneratedPunchCard["group"]>;
};
type ApiPunchCardOption = Omit<
  Partial<EventRegistrationPaymentPunchCardOptionDto>,
  "productId" | "punchCard"
> & {
  productId: string;
  punchCard?: CompatiblePunchCard | null;
};
type ApiPaymentOptions = {
  isFree?: boolean | null;
  payment?: ApiPaymentOption | null;
  dropInStripeProductId?: string | null;
  subscriptionPrices?: ApiPaymentOption[] | null;
  allSubscriptionStatuses?:
    EventRegistrationPaymentOptionsDto["allSubscriptionStatuses"] | null;
  punchCards?: ApiPunchCardOption[] | null;
};

/** An entity form (waiver / intake) attached to a class, surfaced for booking. */
export interface ActivityForm {
  formId: number;
  /** 'waiver' | 'cancelation-agreement' | 'other' */
  type: string;
  requirement?: string;
}

export type SpotAvailability = SpotAvailabilityDto;
export type RoomAvailability = EmbedRoomAvailabilityDto;
export type MonthIndicators = MonthIndicatorsResponseDto;

export interface EmbedSignInResult {
  token: string;
  firstName?: string;
}

function toEmbedSignInResult(result: {
  token: string;
  firstName?: string | null;
}): EmbedSignInResult {
  return {
    token: result.token,
    ...(result.firstName ? { firstName: result.firstName } : {}),
  };
}

/** Request a 6-digit sign-in code by email (always resolves; never reveals match). */
export function requestSignInCode(
  orgId: number,
  email: string,
): Promise<{ sent: boolean }> {
  return getEmbeddedApiClients(orgId).publicEmbeds.requestSignInCode({
    orgId,
    embedRequestCodeDto: { email },
  });
}

/** Verify a sign-in code → customer-scoped JWT (throws on bad/expired code). */
export async function verifySignInCode(
  orgId: number,
  email: string,
  code: string,
): Promise<EmbedSignInResult> {
  return toEmbedSignInResult(
    await getEmbeddedApiClients(orgId).publicEmbeds.verifySignInCode({
      orgId,
      embedVerifyCodeDto: { email, code },
    }),
  );
}

/** Password sign-in → customer-scoped JWT. */
export async function signInWithPassword(
  orgId: number,
  email: string,
  password: string,
): Promise<EmbedSignInResult> {
  return toEmbedSignInResult(
    await getEmbeddedApiClients(orgId).publicEmbeds.loginWithPassword({
      orgId,
      embedLoginDto: { email, password },
    }),
  );
}

/** C723-14/15/16: activate guest stub account + return JWT. */
export async function createEmbedAccount(
  orgId: number,
  body: {
    email: string;
    password: string;
    firstName?: string;
    lastName?: string;
  },
): Promise<EmbedSignInResult> {
  return toEmbedSignInResult(
    await getEmbeddedApiClients(orgId).publicEmbeds.createAccount({
      orgId,
      embedCreateAccountDto: body,
    }),
  );
}

/** C723-17: sync phone to roster profile (requires embed JWT). */
export async function updateMemberPhone(
  orgId: number,
  phone: string,
): Promise<void> {
  await getEmbeddedApiClients(orgId).publicEmbeds.updateMemberPhone({
    orgId,
    embedUpdatePhoneDto: { phone },
  });
}

// ── Member context (Phase 4 — signed-in personalization) ──
// ADDITIVE: this is only ever called when a customer JWT is present. Guests
// never reach it (callers gate on a token first), and it degrades to `null` on
// any error so the guest checkout path is wholly unaffected.

/** A card the signed-in member has on file (for the saved-card picker). */
export interface SavedCard {
  /** Stripe PaymentMethod id (`pm_…`). */
  paymentMethodId: string;
  brand: string; // 'visa' | 'mastercard' | …
  last4: string;
  expMonth?: number;
  expYear?: number;
  isDefault?: boolean;
}

/** A redeemable credit/pass the member holds (membership or class pack). */
export interface MemberCredit {
  /** Punch-card / membership product id backing the credit. */
  productId: string;
  label: string;
  remaining: number | null; // null = unlimited (active membership)
}

/** Everything the signed-in member needs for a personalized checkout. */
export interface MemberContext {
  rosterId: number;
  userId: number;
  stripeCustomerId: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  savedCards: SavedCard[];
  credits: MemberCredit[];
  hasPriorPurchases?: boolean;
  familyMembers: EmbedFamilyMemberWithRoster[];
}

/** Family dependent with roster id (shop purchase beneficiary). */
export type EmbedFamilyMemberWithRoster = EmbedFamilyMember & {
  rosterId: number;
};

/**
 * Load the signed-in member's checkout context (saved cards + credits) from the
 * public member endpoint, authenticated by the embed JWT. Returns `null` when
 * not signed in or on any failure — callers then fall back to the guest flow.
 */
export async function fetchMemberContext(
  orgId: number,
): Promise<MemberContext | null> {
  if (!authHeaders(orgId).Authorization) return null; // not signed in → guest path
  try {
    const m: EmbedMemberContextDto = await getEmbeddedApiClients(
      orgId,
    ).publicEmbeds.getMemberContext({ orgId });
    return {
      rosterId: m.rosterId,
      userId: m.userId,
      stripeCustomerId: m.stripeCustomerId,
      firstName: m.firstName ?? undefined,
      lastName: m.lastName ?? undefined,
      email: m.email ?? undefined,
      phone: m.phone ?? undefined,
      hasPriorPurchases: !!m.hasPriorPurchases,
      savedCards: m.savedCards
        .filter((c) => !!c.paymentMethodId && !!c.last4)
        .map((c) => ({
          paymentMethodId: c.paymentMethodId,
          brand: c.brand || "card",
          last4: c.last4,
          expMonth: c.expMonth ?? undefined,
          expYear: c.expYear ?? undefined,
          isDefault: c.isDefault,
        })),
      credits: m.credits
        .filter((c) => !!c.productId)
        .map((c) => ({
          productId: c.productId,
          label: c.label || "Credit",
          remaining: c.remaining ?? null,
        })),
      familyMembers: (m.familyMembers ?? []).map((f) => ({
        userId: f.userId,
        rosterId: f.rosterId,
        firstName: f.firstName ?? "",
        lastName: f.lastName ?? "",
      })),
    };
  } catch {
    return null; // endpoint missing / expired token / offline → guest path
  }
}

// ── Event registration context (signed-in: prior waiver / forms) ──
// The authed registration-context tells us, per required form (waiver/intake),
// whether the signed-in member has ALREADY completed it (isComplete) and when
// (lastSubmissionDate) — so the booking flow can skip re-asking. Only called
// when signed in (JWT present) with a real event/activity.

export interface RegFormData {
  formId: number;
  /** 'waiver' | 'cancelation-agreement' | 'other' */
  type: string;
  /** Requirement level (e.g. 'required' | 'optional'). */
  requirement?: string;
  label?: string;
  isComplete: boolean;
  lastSubmissionDate?: string;
}
/** A signed-in member's family member (for the "booking for someone else" list). */
export interface EmbedFamilyMember {
  userId: number;
  firstName: string;
  lastName: string;
}

/** The full registration context the customer app uses: forms + the member's
 *  activity-scoped pay options (punch cards), family, and account credit. */
export interface RegContext {
  /** Selected activity payment options, including its resolved drop-in product. */
  paymentOptions?: ApiPaymentOptions;
  formData: RegFormData[];
  credits: MemberCreditOption[];
  guestPassCredits?: MemberCreditOption[];
  /** Canonical owned $0 membership tiers; distinct from spendable credits. */
  ownedAccessProductIds: string[];
  /** Every usable owned membership for this activity (never re-sold). */
  ownedProductIds: string[];
  /** Drop-in price after the member's owned-membership discount, if any. */
  memberDropInPrice?: number;
  deferredMemberships?: OwnedAccess['deferredMemberships'];
  familyMembers: EmbedFamilyMember[];
  availableCreditBalance: number;
}

function toMemberCredit(pc: ApiPunchCardOption): MemberCreditOption {
  return {
    productId: pc.productId,
    productName: pc.punchCard?.product?.name,
    isUnlimited: pc.isUnlimited ?? undefined,
    numberOfUsesRemaining: pc.numberOfUsesRemaining ?? 0,
    hasCredit: !!pc.credit,
    isAvailableForUse: pc.isAvailableForUse ?? undefined,
    // Borrowed cards retain the existing product-resolution path; explicit
    // selections here identify the signed-in member's own allotment.
    punchCardId: pc.punchCardUserId ? undefined : pc.punchCard?.id,
    creditId: pc.credit?.id,
    // `created`/`neverExpires` are part of the credit-expiration feature that
    // only exists on main; release has no such fields on EventPunchCardCredit.
    // Left unset so member-credits ranking falls back to expiration dates.
    creditExpirationDate: pc.creditExpirationDate,
    isWithinCreditExpiration: pc.isWithinCreditExpiration ?? undefined,
    preventBookingOutsideCreditExpiration:
      pc.preventBookingOutsideCreditExpiration ?? undefined,
    isWithinBillingPeriod: pc.isWithinBillingPeriod ?? undefined,
    preventBookingOutsideSubscriptionWindow:
      pc.preventBookingOutsideSubscriptionWindow ?? undefined,
    preventBookingOutsideContractWindow:
      pc.preventBookingOutsideContractWindow ?? undefined,
    isPaused: pc.isPaused ?? undefined,
    isBookAheadCredit: pc.isBookAheadCredit ?? undefined,
    isBookAheadEligible: pc.isBookAheadEligible ?? undefined,
    guestsOnly: pc.punchCard?.group?.guestsOnly ?? undefined,
    disableFamilySharing:
      pc.punchCard?.product?.disableFamilySharing ?? undefined,
    disableGuestSharing:
      pc.punchCard?.product?.disableGuestSharing ?? undefined,
  };
}


/** PLAT-6791: dashboard-parity owned membership / All Access verdict. */
function memberAccessFields(
  po: ApiPaymentOptions,
): Pick<
  RegContext,
  "ownedAccessProductIds" | "ownedProductIds" | "memberDropInPrice" | "deferredMemberships"
> {
  const access = ownedAccess(po);
  return {
    ownedAccessProductIds: access.freeProductIds,
    ownedProductIds: access.ownedProductIds,
    ...(access.deferredMemberships ? { deferredMemberships: access.deferredMemberships } : {}),
    ...(access.memberDropInPrice === undefined
      ? {}
      : { memberDropInPrice: access.memberDropInPrice }),
  };
}

/**
 * Fetch the signed-in member's registration context for an activity: required
 * forms, the member's activity-scoped credit-pack options (same source the
 * customer app uses to prioritize), family members, and account credit balance.
 * Returns null when not signed in / on any failure (caller falls back to asking).
 */
export async function fetchEventRegistrationContext(
  orgId: number,
  orgSlug: string,
  eventSlug: string,
  activityId: string,
  targetUserId: number,
): Promise<RegContext | null> {
  if (!authHeaders(orgId).Authorization) return null;
  try {
    const ctx: EventRegistrationContextDto = await getEmbeddedApiClients(
      orgId,
    ).events.getEventRegistrationContext({
      orgSlug,
      eventSlug,
      activityId,
      getEventRegistrationContextRequestDto: {
        orgSlug,
        eventSlug,
        activityId,
        targetUserId,
        includeGuestPasses: true,
      },
    });
    return {
      paymentOptions: ctx.paymentOptions,
      formData: (ctx.formData ?? []).map((form) => ({
        formId: form.formId,
        type: form.type,
        requirement: form.requirement,
        label: form.label ?? undefined,
        isComplete: form.isComplete,
        lastSubmissionDate: form.lastSubmissionDate ?? undefined,
      })),
      credits: ctx.paymentOptions.punchCards
        .filter((pc) => pc.relevantToTargetEvent !== false)
        .map(toMemberCredit),
      guestPassCredits: (ctx.guestPasses ?? []).map(toMemberCredit),
      ...memberAccessFields(ctx.paymentOptions),
      familyMembers: (ctx.familyMemberRegistrations ?? []).map((f) => ({
        userId: f.userId,
        firstName: f.firstName ?? "",
        lastName: f.lastName ?? "",
      })),
      availableCreditBalance: ctx.availableCreditBalance ?? 0,
    };
  } catch {
    return null;
  }
}

// ── Appointments ──
// Reuses the SAME anonymous schedule endpoints multisite books against (no new
// embed backend): /schedule/all/configurations for providers + appointment
// types, /orgs/:orgId/events-filter-data for provider names, and
// /schedule/:orgId/:providerUserId/availabilities for slots. See the
// `project-embed-appointments-backend` memory for the full contract.
import type {
  AppointmentGroupTag,
  AppointmentService,
  AppointmentProvider,
  DaySlots,
} from "../components/data/mock-appointments";

export interface AppointmentData {
  services: AppointmentService[];
  providers: AppointmentProvider[];
  /** appointmentTypeId → providerUserIds that offer it. */
  providersByService: Record<string, number[]>;
  /** Org/schedule IANA timezone for slot display. */
  orgTimezone: string;
  /**
   * PLAT-5624: the org's mirrored group roster
   * (`org.attributes.appointmentGroups`).
   *
   * **Ordering metadata ONLY — never an allow-list.** `session-org-anon`
   * returns `OrgForSessionDto.forNone()` for any org without a *published
   * site* (`dashboard.service.getOrgForSessionForOrgId`), so an embed-only
   * customer legitimately gets `[]` here while still having fully-tagged
   * appointment types. Groups are therefore derived from the services
   * themselves; this list only decides what order they appear in.
   */
  orgGroups: AppointmentGroupTag[];
}

/**
 * Load the org's appointment services + providers from the public schedule
 * configuration, resolving provider names from the (anonymous) events filter
 * data. Each service carries its real Stripe product id + price for checkout.
 */
export async function fetchAppointmentData(
  orgId: number,
): Promise<AppointmentData> {
  const api = getEmbeddedApiClients(orgId);
  const [configs, filters, orgSession] = await Promise.all([
    api.schedule.getScheduleConfigurationsForOrg({
      orgIds: String(orgId),
      includeArchived: false,
    }),
    fetchFilterData(orgId).catch(
      (): EventFilterDataDto | undefined => undefined,
    ),
    api.dashboard
      .getOrgForAnonSession({ orgId, host: "embed" })
      .catch((): OrgForSessionDto | undefined => undefined),
  ]);
  const nameOf = nameMap(filters?.providerUsers);
  const photoOf = photoMap(filters?.providerUsers);

  // Appointment types live in each provider's ScheduleConfiguration JSON.
  // Preserve one row per provider even when legacy/cloned data reuses the same
  // appointment-type id: price, duration and Stripe product are provider-owned
  // and collapsing on bare `at.id` can charge the later provider from the
  // first provider's stale row.
  const serviceByProviderAndId = new Map<string, AppointmentService>();
  const providersByService = new Map<string, Set<number>>();
  const providerIds = new Set<number>();
  let orgTimezone = orgSession?.org?.attributes?.timezone ?? "";

  for (const cfg of configs) {
    if (cfg.orgId !== orgId) continue;
    const scheduleLabels = new Map(
      (cfg.schedules ?? []).map((schedule) => [schedule.id, schedule.label]),
    );
    const scheduleTz = cfg.schedules?.find((s) => s.timezone)?.timezone;
    if (scheduleTz && !orgTimezone) {
      orgTimezone = scheduleTz;
    }
    for (const at of cfg.appointmentTypes ?? []) {
      if (at.deleted || at.showInApp === false || at.blockScheduleTime === true)
        continue;
      providerIds.add(cfg.userId);
      const groups = normalizeGroupTags(at.groups);
      const serviceKey = `${cfg.id}:${cfg.userId}:${at.scheduleId}:${at.id}`;
      if (!serviceByProviderAndId.has(serviceKey)) {
        serviceByProviderAndId.set(serviceKey, {
          id: at.id,
          offeringKey: serviceKey,
          scheduleConfigurationId: cfg.id,
          scheduleId: at.scheduleId,
          scheduleLabel: scheduleLabels.get(at.scheduleId),
          sharedAppointmentTypeId: at.sharedAppointmentTypeId ?? undefined,
          sharedAppointmentTypeDurationId:
            at.sharedAppointmentTypeDurationId ?? undefined,
          sharedAppointmentTypeRevision:
            at.sharedAppointmentTypeRevision ?? undefined,
          category: "Services",
          name: at.label,
          duration: at.durationInMinutes ?? 30,
          price: at.price ?? 0,
          // APT-35: real service description (the API DOES expose it on
          // AppointmentTypeDto.description) — was previously hardcoded to '',
          // which suppressed the appointment card's expand caret + drawer.
          description: at.description ?? "",
          stripeProductId: at.stripeProductId ?? undefined,
          // PLAT-5624: carry the org's explicit free flag. It was declared on
          // ApiAppointmentType but never mapped, so downstream code only had
          // `price` to go on — and `price: 0` is ALSO what a credit-priced
          // service looks like. See AppointmentService.isFree.
          isFree: at.isFree,
          formId: at.formId,
          // The type's real forms. `AppointmentTypeDto`'s constructor migrates
          // a legacy `formId` into this array (typed as a waiver) and then
          // sets `formId = 0`, so `formId` alone resolves to nothing on live
          // data — reading it as the form id meant an appointment's waiver was
          // never fetched. Same shape and same `!!formId` guard the class and
          // event feeds use, so the checkout receives one consistent list.
          forms: (at.forms ?? [])
            .filter((f) => !!f.formId)
            .map((f) => ({
              formId: f.formId,
              type: f.type || "other",
              requirement: f.requirement,
            })),
          allowedParticipants: at.allowedParticipants ?? 1,
          allowBuyout: at.allowBuyout === true,
          buyoutPrice:
            typeof at.buyoutPrice === "number" ? at.buyoutPrice : undefined,
          buyoutCurrency: orgSession?.org?.attributes?.currency || undefined,
          // APT-32: service photo (appointment type imageUrl) → the `thumbnail`
          // field the appointments component renders. Undefined when unset.
          thumbnail: at.imageUrl || undefined,
          // PLAT-5624: group tags + owning provider, so step 1 can merge the
          // per-provider copies of a type into a single group card.
          groups: groups.length ? groups : undefined,
          providerUserId: cfg.userId,
        });
      } else if (groups.length) {
        // Duplicate row inside the SAME provider config: preserve one service
        // but union its title-based tags.
        const existing = serviceByProviderAndId.get(serviceKey)!;
        const seen = new Set((existing.groups ?? []).map((g) => g.title));
        const added = groups.filter((g) => !seen.has(g.title));
        if (added.length)
          existing.groups = [...(existing.groups ?? []), ...added];
      }
      if (!providersByService.has(at.id))
        providersByService.set(at.id, new Set());
      providersByService.get(at.id)!.add(cfg.userId);
    }
  }

  const providers: AppointmentProvider[] = [
    { id: "any", name: "First Available", generic: true },
    ...[...providerIds].map((uid) => ({
      id: String(uid),
      name: nameOf[uid] || "Provider",
      // APT-33: staff headshot (profileAvatarImageUrl) → the provider `photo`
      // field the appointments component renders. Undefined falls back to
      // initials in the component.
      photo: photoOf[uid] || undefined,
    })),
  ];

  return {
    services: [...serviceByProviderAndId.values()],
    providers,
    providersByService: Object.fromEntries(
      [...providersByService].map(([k, v]) => [k, [...v]]),
    ),
    orgTimezone: orgTimezone || "UTC",
    orgGroups: normalizeGroupTags(
      orgSession?.org?.attributes?.appointmentGroups,
    ),
  };
}

/**
 * PLAT-5624: trim + drop untitled group tags. Groups are matched by title, so
 * a blank title is unusable (and would bucket unrelated types together).
 */
function normalizeGroupTags(
  raw: ReadonlyArray<Partial<AppointmentTypeGroupDto>> | null | undefined,
): AppointmentGroupTag[] {
  const out: AppointmentGroupTag[] = [];
  const seen = new Set<string>();
  for (const g of raw ?? []) {
    const title = (g?.title ?? "").trim();
    if (!title || seen.has(title)) continue;
    seen.add(title);
    out.push({ id: g.id, title, orgId: g.orgId });
  }
  return out;
}

function orgLocalDayKey(iso: string, tz: string): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date(iso));
  const y = parts.find((p) => p.type === "year")?.value ?? "0000";
  const m = parts.find((p) => p.type === "month")?.value ?? "01";
  const d = parts.find((p) => p.type === "day")?.value ?? "01";
  return `${y}-${m}-${d}`;
}

export function orgLocalHour(iso: string, tz: string): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    hour: "numeric",
    hour12: false,
  }).formatToParts(new Date(iso));
  return Number(parts.find((p) => p.type === "hour")?.value ?? 0);
}

/** Org-local calendar day key for a wall date shown in the picker. */
export function orgWallDayKey(d: Date, tz: string): string {
  const anchor = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 12, 0, 0);
  return orgLocalDayKey(anchor.toISOString(), tz);
}

/**
 * Real availability for a provider + appointment type over a date range,
 * grouped into morning/afternoon/evening for the slot picker. Returns the raw
 * timeslots too so the booking can submit the exact `{id,startTime,endTime}`.
 */
export async function fetchAppointmentAvailability(
  orgId: number,
  providerUserId: number,
  appointmentTypeId: string,
  startIso: string,
  endIso: string,
  orgTimezone = "UTC",
): Promise<{ slots: ScheduleTimeslotDto[]; byDay: Record<string, DaySlots> }> {
  const raw = await getEmbeddedApiClients(
    orgId,
  ).schedule.getScheduleAvailabilities({
    orgId,
    providerUserId,
    appointmentTypeId,
    startDate: startIso,
    endDate: endIso,
  });
  // Older deployments wrapped this array in `{ availabilities }`; preserve
  // that rollout compatibility while the generated contract is now canonical.
  const slots: ScheduleTimeslotDto[] = Array.isArray(raw)
    ? raw
    : ((raw as unknown as { availabilities?: ScheduleTimeslotDto[] })
        .availabilities ?? []);

  const byDay: Record<string, DaySlots> = {};
  for (const s of slots) {
    const d = new Date(s.startTime);
    if (Number.isNaN(d.getTime())) continue;
    const dayKey = orgLocalDayKey(s.startTime, orgTimezone);
    const label = fmtTime(s.startTime, orgTimezone);
    const hour = orgLocalHour(s.startTime, orgTimezone);
    const part: keyof DaySlots =
      hour < 12 ? "morning" : hour < 17 ? "afternoon" : "evening";
    (byDay[dayKey] ??= { morning: [], afternoon: [], evening: [] })[part].push(
      label,
    );
  }
  return { slots, byDay };
}

/**
 * PLAT-6790: open-slot counts per org-local day for a range, plus the slots of
 * `day` (org-local `YYYY-MM-DD`) when given. Lets the date strip light up a
 * month of days without shipping every slot — thousands for a busy provider.
 */
export async function fetchAppointmentAvailabilityDays(
  orgId: number,
  providerUserId: number,
  appointmentTypeId: string,
  startIso: string,
  endIso: string,
  orgTimezone = "UTC",
  day?: string,
): Promise<{
  counts: Record<string, number>;
  slots: ScheduleTimeslotDto[];
}> {
  const res = await getEmbeddedApiClients(
    orgId,
  ).schedule.getScheduleAvailabilityDays({
    orgId,
    providerUserId,
    appointmentTypeId,
    startDate: startIso,
    endDate: endIso,
    timeZone: orgTimezone,
    ...(day ? { day } : {}),
  });
  const counts: Record<string, number> = {};
  for (const d of res.days ?? []) counts[d.date] = d.count;
  return { counts, slots: res.slots ?? [] };
}

/** Format an ISO instant in the org IANA timezone (appointment slot labels). */
export function formatOrgLocalTime(iso: string, tz: string): string {
  return fmtTime(iso, tz);
}

/** Org-local calendar day key (`YYYY-MM-DD`) for grouping appointment slots. */
export function orgAppointmentDayKey(iso: string, tz: string): string {
  return orgLocalDayKey(iso, tz);
}

/** Credits applicable to this appointment type (payment-options only). */
export function appointmentCreditsFromContext(ctx: {
  paymentOptions?: ApiPaymentOptions;
}): MemberCreditOption[] {
  return (ctx.paymentOptions?.punchCards ?? [])
    .filter((pc) => pc.relevantToTargetEvent !== false)
    .map(toMemberCredit);
}

/** Signed-in appointment pay options (credits + purchasable tiers). */
export async function fetchAppointmentRegistrationContext(
  orgId: number,
  providerUserId: number,
  appointmentTypeId: string,
  targetUserId: number,
  appointmentDate?: string,
): Promise<{
  paymentOptions: AppointmentPaymentOptionsDto;
  credits: MemberCreditOption[];
} | null> {
  if (!authHeaders(orgId).Authorization) return null;
  try {
    const ctx: AppointmentRegistrationContextDto = await getEmbeddedApiClients(
      orgId,
    ).schedule.getAppointmentRegistrationContext({
      orgId,
      providerUserId,
      appointmentTypeId,
      getAppointmentRegistrationContextRequestDto: {
        orgId,
        providerUserId,
        appointmentTypeId,
        targetUserId,
        ...(appointmentDate ? { appointmentDate } : {}),
      },
    });
    return {
      paymentOptions: ctx.paymentOptions,
      credits: appointmentCreditsFromContext(ctx),
    };
  } catch {
    return null;
  }
}

/** Prepare payment options only for the guest's selected class activity. */
export async function fetchClassPaymentOptionsAnon(
  orgId: number,
  orgSlug: string,
  eventSlug: string,
  activityId: string,
): Promise<ApiPaymentOptions | null> {
  try {
    return await getEmbeddedApiClients(orgId).events.getPaymentOptionsNoAuth({
      orgSlug,
      eventSlug,
      activityId,
    });
  } catch {
    return null;
  }
}

/**
 * PLAT-5624: appointment pay options for an ANONYMOUS visitor.
 *
 * This is the gap that made a priced appointment unbookable through the embed.
 * `AppointmentTypeDto.stripeProductId` is created LAZILY — `getPaymentOptions`
 * (via `getProductForAppointmentDropIn`) is what mints the drop-in Stripe
 * product and writes the id back onto the appointment type. Until something
 * calls it, the public `/schedule/all/configurations` feed carries
 * `stripeProductId: undefined`, so:
 *
 *  - a flat-priced service reached `<zipper-payment>` with an item whose
 *    `stripeProductId` was empty → `payable` false → "This item isn't available
 *    for online purchase yet." with only a Close button; and
 *  - a credit-priced service (whose cost lives on a credit-pack product, so the
 *    appointment type's own `price` is 0) had no punch cards or subscription
 *    prices to offer at all → the checkout showed an empty "Choose how to pay"
 *    and a `Book appointment · Free` CTA.
 *
 * The signed-in flow never hit either because it calls
 * `fetchAppointmentRegistrationContext`, which resolves the same options.
 * `payment-options-noauth` is the existing anonymous twin of that endpoint
 * (`schedule.controller.ts`, `@ZApiAnonRoute.Post`), so guests can resolve
 * exactly what members already do — no new backend surface.
 *
 * Returns null on any failure. Callers MUST fail closed on null (refuse to
 * book) rather than treating "no options" as "free".
 */
export async function fetchAppointmentPaymentOptionsAnon(
  orgId: number,
  providerUserId: number,
  appointmentTypeId: string,
  appointmentDate?: string,
): Promise<AppointmentPaymentOptionsDto | null> {
  try {
    return await getEmbeddedApiClients(orgId).schedule.getPaymentOptionsNoAuth({
      orgId,
      providerUserId,
      appointmentTypeId,
      ...(appointmentDate ? { appointmentDate } : {}),
    });
  } catch {
    return null;
  }
}

// ── Appointment intake form (guest identity) ──
// The no-auth appointment create is form-driven: a guest establishes identity by
// submitting the appointment type's intake form, yielding a formResponseId the
// booking references. Fetch the form, submit answers anonymously, then create.

export interface FormQuestion {
  key: string;
  label: string;
  /** 'text' | 'email' | 'first-name' | 'last-name' | 'phone-number' | 'date' | 'textarea' | 'dropdown' | 'single-select' | 'multi-select' | 'signature' */
  type: string;
  required: boolean;
  options: string[];
  /**
   * The question's own rich-text body. This is where providers actually author
   * waiver and cancellation-agreement copy — 12 of 14 waiver forms on staging
   * have an EMPTY `content.details` and put the real text on the signature
   * question's `detailsHtml`. The platform's own renderer reads this field
   * (libs/shared-ux/.../AthleteFormView.tsx), and the embed previously dropped
   * it, which is why class checkout showed an empty waiver body.
   *
   * Passed through VERBATIM (not via stripHtml) so the consumer can preserve
   * paragraph breaks — stripHtml collapses all whitespace and would flatten
   * authored copy into one run-on block.
   */
  detailsHtml?: string;
}
export interface EmbedForm {
  id: number;
  name: string;
  /** Org-authored legal/informational body text (e.g. the actual liability
   * waiver language), stripped of HTML. Previously dropped entirely, so a
   * real waiver form rendered only its input questions (often just a bare
   * signature textarea) with no waiver text above it. */
  details?: string;
  questions: FormQuestion[];
}

/** Fetch an appointment type's intake form (public). Returns null if none/error. */
export async function fetchAppointmentForm(
  orgId: number,
  formId: number,
): Promise<EmbedForm | null> {
  try {
    const forms: FormDto[] = await getEmbeddedApiClients(
      orgId,
    ).forms.getFormsPublic({
      orgId,
      formIds: String(formId),
    });
    const f = forms[0];
    if (!f) return null;
    return {
      id: f.id,
      name: f.content.name || "Your details",
      details: stripHtml(f.content.details) || undefined,
      questions: f.content.questions
        .filter((q) => !q.deleted && q.type !== 'captcha')
        .map((q) => ({
          key: q.key,
          label: q.label,
          type: q.type,
          required: !!q.metadata?.required,
          // Verbatim — see FormQuestion.detailsHtml.
          detailsHtml: q.detailsHtml || undefined,
          options: (q.metadata?.options ?? [])
            .map((o) => o.value)
            .filter(Boolean),
        })),
    };
  } catch {
    return null;
  }
}

/**
 * Identity for an anonymous form submission — the guest roster this response
 * belongs to, plus the capability that proves the caller just created it.
 *
 * Submitting without one stores the response with no `userId`: it exists in
 * Forms but is attached to nobody, so "require once" can never find the prior
 * signature and the roster reads as unsigned. Callers that have a roster (or
 * can create one first) MUST pass it.
 */
export type FormSubmitIdentity =
  | {
      kind: "member";
      /**
       * An authenticated guardian may file a response for their dependent.
       * The API verifies that relationship before persisting the response.
       */
      targetUserId?: number;
    }
  | { kind: "guest"; rosterId: number; checkoutToken: string };

/**
 * Which of these forms the SIGNED-IN customer already has on file.
 *
 * Guests never need this (a brand-new roster has nothing on file), so callers
 * skip it when signed out. Returns an empty set on any failure, which means
 * "ask again" — the safe direction, since re-asking is an inconvenience while
 * wrongly skipping lands the customer on a rejected charge.
 */
export async function fetchFormsOnFile(
  orgId: number,
  formIds: number[],
): Promise<Map<number, string | null>> {
  const ids = [...new Set(formIds.filter((id) => !!id))];
  if (!ids.length) return new Map();
  try {
    const rows = await getEmbeddedApiClients(orgId).forms.getFormsOnFile({
      orgId,
      formIds: ids.join(","),
    });
    // Keyed by form id so `has()` still answers "already signed?", with the
    // response id alongside for callers that must record WHICH signature they
    // skipped on. An API that answers `isOnFile` without an id (older
    // deployment) still reads as on file — the skip is the behaviour, the link
    // is the bonus.
    return new Map(
      rows
        .filter((r) => r.isOnFile)
        .map((r) => [r.formId, r.formResponseId || null]),
    );
  } catch {
    return new Map();
  }
}

/** Submit an intake-form response; returns the formResponseId (or null). */
export async function submitAppointmentForm(
  orgId: number,
  formId: number,
  answers: { questionKey: string; value: string }[],
  identity?: FormSubmitIdentity | undefined,
): Promise<string | null> {
  try {
    const form = await fetchAppointmentForm(orgId, formId);
    if (!form) return null;
    // A signed-in customer writes under their own session unless a guardian
    // names their dependent. The API authorizes that relationship before it
    // stores the response, so the answer and mapped custom fields belong to
    // the person attending rather than the booker.
    if (identity?.kind === "member") {
      const res = await getEmbeddedApiClients(orgId).forms.saveResponse({
        id: formId,
        orgId,
        saveFormResponseDto: {
          token: "",
          answers,
          skipEmail: true,
          ...(identity.targetUserId
            ? { userId: identity.targetUserId }
            : {}),
        },
      });
      return res.formResponseId ?? null;
    }
    const res = await getEmbeddedApiClients(orgId).forms.saveResponseAnonymous({
      id: formId,
      orgId,
      saveFormResponseDto: {
        token: "",
        answers,
        skipEmail: true,
        ...(identity
          ? {
              rosterId: identity.rosterId,
              checkoutToken: identity.checkoutToken,
            }
          : {}),
      },
    });
    return res.formResponseId ?? null;
  } catch {
    return null;
  }
}

function isoRange(
  daysAhead: number,
  daysBack = 0,
): { minDate: string; maxDate: string } {
  const now = new Date();
  const min = new Date(now);
  min.setDate(min.getDate() - daysBack);
  const max = new Date(now);
  max.setDate(max.getDate() + daysAhead);
  return { minDate: min.toISOString(), maxDate: max.toISOString() };
}

function fmtTime(iso?: string, tz?: string): string {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      timeZone: tz,
    });
  } catch {
    return "";
  }
}

function durationMin(start?: string, end?: string): string {
  if (!start || !end) return "";
  const ms = new Date(end).getTime() - new Date(start).getTime();
  const min = Math.round(ms / 60000);
  return min > 0 ? `${min} min` : "";
}

const NAMED_TEXT_ENTITIES: Readonly<Record<string, string>> = {
  nbsp: " ",
  ensp: " ",
  emsp: " ",
  thinsp: " ",
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'",
  ndash: "–",
  mdash: "—",
  hellip: "…",
  lsquo: "‘",
  rsquo: "’",
  ldquo: "“",
  rdquo: "”",
  bull: "•",
  middot: "·",
  copy: "©",
  reg: "®",
  trade: "™",
};

export function stripHtml(html?: string): string {
  if (!html) return "";
  return (
    html
      .replace(/<[^>]+>/g, " ")
      // One replacement pass is deliberate: `&amp;nbsp;` becomes the literal
      // text `&nbsp;`, never recursively decoded into a space.
      .replace(
        /&(?:#(\d+)|#x([0-9a-f]+)|([a-z]+));/gi,
        (entity, decimal: string, hex: string, named: string) => {
          if (named) return NAMED_TEXT_ENTITIES[named.toLowerCase()] ?? entity;
          const codePoint = Number.parseInt(decimal ?? hex, decimal ? 10 : 16);
          if (
            !Number.isInteger(codePoint) ||
            codePoint <= 0 ||
            codePoint > 0x10ffff ||
            (codePoint >= 0xd800 && codePoint <= 0xdfff)
          ) {
            return entity;
          }
          return String.fromCodePoint(codePoint);
        },
      )
      .replace(/[\s\u00a0]+/g, " ")
      .trim()
  );
}

/** Plain-text full descriptions retain authored block and line boundaries.
 * Consumers interpolate this as escaped text, never as authored HTML. */
export function toDescriptionText(html?: string): string {
  if (!html) return "";
  const source = /<[^>]+>/.test(html) ? html.replace(/[\r\n]+/g, " ") : html;
  return source
    .replace(/<(script|style)\b[^>]*>[\s\S]*?<\/\1\s*>/gi, "")
    .replace(/<br\b[^>]*>/gi, "\n")
    .replace(
      /<\/(?:p|div|h[1-6]|li|tr|blockquote|section|article)\s*>/gi,
      "\n\n",
    )
    .replace(/<[^>]+>/g, "")
    .split(/\r?\n/)
    .map((line) => stripHtml(line))
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

// ── Public API + adapters ──

export interface ScheduleData {
  classes: MockClass[];
  /** Booking tiers keyed by class id (from each activity's payment options). */
  tiersByClassId: Record<string, PricingTier[]>;
}

const SCHEDULE_EVENTS_PAGE_SIZE = 500;
const MAX_SCHEDULE_EVENTS = 50_000;

type FetchScheduleEventsParams = {
  orgId: number;
  minDate: string;
  maxDate: string;
  locationId?: string;
  includeOneOffEvents?: boolean;
};

/** API `type` filter for the class-schedule embed (see GetEventsByTypeFilter). */
export function scheduleEmbedTypeFilter(includeOneOffEvents = true): string[] {
  return includeOneOffEvents ? ["class", "event", "program"] : ["class", "program"];
}

export function isStandaloneOneOffEvent(ev: {
  eventGroupingId?: string | null;
  parentEventId?: number | null;
}): boolean {
  return !ev.eventGroupingId && ev.parentEventId == null;
}

async function fetchAllSchedulePages(
  params: FetchScheduleEventsParams,
): Promise<PublicBookingScheduleResponseDto> {
  const schedule: PublicBookingScheduleResponseDto = {
    events: [],
    programs: [],
    registrationCounts: [],
    substitutedEventIds: [],
    hasMore: false,
  };
  const eventApi = getEmbeddedApiClients(params.orgId).events;
  for (
    let skip = 0;
    skip <= MAX_SCHEDULE_EVENTS;
    skip += SCHEDULE_EVENTS_PAGE_SIZE
  ) {
    const page = await eventApi.getPublicBookingSchedule({
      orgId: params.orgId,
      publicBookingScheduleRequestDto: {
        minDate: params.minDate,
        maxDate: params.maxDate,
        take: SCHEDULE_EVENTS_PAGE_SIZE,
        skip,
        type: scheduleEmbedTypeFilter(params.includeOneOffEvents !== false),
        includePaymentOptions: true,
        locationIds: params.locationId ? [params.locationId] : undefined,
      },
    });
    if (schedule.events.length + page.events.length > MAX_SCHEDULE_EVENTS) {
      throw new Error(
        `Schedule exceeds the supported limit of ${MAX_SCHEDULE_EVENTS} events`,
      );
    }
    schedule.events.push(...page.events);
    schedule.programs.push(...(page.programs ?? []));
    schedule.registrationCounts.push(...page.registrationCounts);
    schedule.substitutedEventIds.push(...page.substitutedEventIds);
    if (!page.hasMore) return schedule;
  }

  throw new Error(
    `Schedule exceeds the supported limit of ${MAX_SCHEDULE_EVENTS} events`,
  );
}

export interface ScheduleDateRange {
  minDate: string;
  maxDate: string;
}

export async function fetchSchedule(
  orgId: number,
  locationId?: string,
  dateRange?: ScheduleDateRange,
  includeOneOffEvents = true,
): Promise<ScheduleData> {
  // Fetch a 3-month window so the month view (and month picker) has real data
  // to page through, not just ~3 weeks. Calendar navigation can supply a
  // narrower explicit range for any month without changing this first paint.
  const { minDate, maxDate } = dateRange ?? isoRange(90, 30);
  const [schedule, filters] = await Promise.all([
    fetchAllSchedulePages({
      orgId,
      minDate,
      maxDate,
      locationId,
      includeOneOffEvents,
    }),
    fetchFilterData(orgId).catch(
      (): EventFilterDataDto | undefined => undefined,
    ),
  ]);
  const events = schedule.events;
  const substitutedEventIds = new Set(schedule.substitutedEventIds);
  const registrationCounts = new Map(
    schedule.registrationCounts.map((count) => [
      `${count.eventId}:${count.activityId}`,
      count,
    ]),
  );

  const providerName = nameMap(filters?.providerUsers);
  const providerPhoto = photoMap(filters?.providerUsers);
  const providerBio = bioMap(filters?.providerUsers);
  const locName = locationMap(filters?.locations);
  const locAddr = locationAddressMap(filters?.locations);

  const classes: MockClass[] = [];
  const tiersByClassId: Record<string, PricingTier[]> = {};

  for (const ev of events) {
    const cancelled = ev.status === "canceled";
    for (const act of ev.activities) {
      const externalTicketUrl = act.externalRegistration?.enabled
        ? getSafeExternalTicketUrl(act.externalRegistration.url)
        : undefined;
      const date = new Date(act.start);
      const id = act.id;
      const providerIds = act.providerUserIds ?? [];
      const count = registrationCounts.get(`${ev.id}:${act.id}`);
      const { capacityState, spotsLeft, spotsTotal } = deriveCapacity({
        ...act,
        activeRegistrationCount: count?.numberOfRegistrations,
        waitlistRegistrationCount: count?.waitlistedQuantity ?? undefined,
        privateSessionBooked: count?.privateSessionBooked === true,
      });
      // Instructor bio (SCH-08): the first provider on this class that actually
      // has non-empty biography HTML. Sanitized WYSIWYG HTML is passed through
      // verbatim so the accordion can render its formatting; the `stripHtml`
      // check just guards against empty markup (e.g. "<p></p>").
      const instructorBio = providerIds
        .map((pid) => providerBio[pid])
        .find((b): b is string => !!b && !!stripHtml(b));
      classes.push({
        id,
        ...(externalTicketUrl
          ? { externalTickets: true, externalTicketUrl }
          : {}),
        offeringKind: ev.eventGroupingType === "program" ? "program" : isStandaloneOneOffEvent(ev) ? "event" : "class",
        programName: ev.programName ?? undefined,
        programSessionOrdinal: ev.programSessionOrdinal ?? undefined,
        programSessionCount: ev.programSessionCount ?? undefined,
        programBookingUrl: ev.programBookingUrl ?? undefined,
        date,
        time: fmtTime(act.start, act.timezone),
        duration: durationMin(act.start, act.end),
        name: ev.name,
        color: ev.details?.color ?? undefined,
        instructor: providerIds
          .map((pid) => providerName[pid])
          .filter(Boolean)
          .join(", "),
        location:
          ev.details?.location?.name ||
          (ev.locationId ? locName[ev.locationId] : "") ||
          "",
        locationAddress:
          (ev.locationId ? locAddr[ev.locationId] : undefined) || undefined,
        capacityState,
        spotsLeft,
        spotsTotal,
        buyoutAvailable: count?.buyoutAvailable ?? false,
        buyoutPriceMinor: count?.buyoutPriceMinor ?? undefined,
        buyoutCurrency: count?.buyoutCurrency ?? undefined,
        cancelled,
        cancelReason: cancelled
          ? (ev.details?.cancellationNote ?? undefined)
          : undefined,
        stripeProductId: act.stripeProductId ?? undefined,
        spotBookingEnabled:
          !!act.spotBooking?.enabled && !!act.spotBooking?.roomId,
        roomId: act.spotBooking?.roomId ?? undefined,
        startIso: act.start,
        endIso: act.end,
        openRegistrationTimeFrame: act.openRegistrationTimeFrame === true,
        registrationOpenAt: act.openRegistrationTimeFrame
          ? undefined
          : act.registrationOpenAt,
        registrationClosesAt: act.registrationClosesAt,
        // N2: the activity's location timezone — drives the default (location)
        // side of the schedule's timezone switcher.
        timezone: act.timezone,
        eventSlug: ev.slug,
        forms: (act.forms ?? [])
          .filter((f) => !!f.formId)
          .map((f) => ({
            formId: f.formId,
            type: f.type || "other",
            requirement: f.requirement,
          })),
        // Instructor headshot (for the default 'instructor' thumbnail mode) —
        // the provider's profileAvatarImageUrl from the filter-data payload.
        // Falls back to initials in the component when no avatar is set.
        photo: providerIds.map((pid) => providerPhoto[pid]).find(Boolean),
        // Class/session image (for the 'class' thumbnail option + checkout
        // card) comes straight off the bulk list response. The description
        // does not: this endpoint always blanks `contentHtml` (see
        // EventDto.purgeExcessDetails), so it's fetched on demand via
        // fetchEventDescription() only when a row's expand chevron opens.
        classPhoto: ev.details?.headerImageUrl || undefined,
        hasDescription: !!ev.details?.hasDetails,
        // Instructor bio (SCH-08): sanitized WYSIWYG HTML for the first
        // instructor that has one; `hasInstructorBio` is the boolean the
        // schedule gates the bio-accordion affordance on.
        instructorBio,
        hasInstructorBio: !!instructorBio,
        // ST1 pre-filter ids: event-grouping (settings' class filter), provider
        // user ids (instructor filter), and location id (location filter). The
        // event-grouping id lives on the event, not the activity.
        eventGroupingId: ev.eventGroupingId ?? undefined,
        isStandaloneOneOff: isStandaloneOneOffEvent(ev),
        providerUserIds: providerIds,
        locationId: ev.locationId ?? undefined,
        hasSubstitute: substitutedEventIds.has(ev.id),
      });
      tiersByClassId[id] = externalTicketUrl
        ? []
        : tiersFromPaymentOptions(act.paymentOptions ?? undefined);
    }
  }
  classes.sort((a, b) => a.date.getTime() - b.date.getTime());
  return { classes, tiersByClassId };
}

/** On-demand fetch for a single class/session's plain-text description,
 * called when a schedule row's expand chevron opens. Uses the same public
 * single-event endpoint the web/multisite apps already call for this via
 * `getEventDetailsClientside()` (apps/web/app/shared/src/utilities/api/events-utils.ts) —
 * unlike the bulk list endpoint, it does not blank `contentHtml`. */
export async function fetchEventDescription(
  orgId: number,
  eventSlug: string,
): Promise<string> {
  const event = await getEmbeddedApiClients(orgId).events.getEventBySlug({
    orgIdOrSlug: String(orgId),
    slug: eventSlug,
  });
  return toDescriptionText(event.details?.contentHtml);
}

/**
 * Request-side type filter for the events embed: one-off events **and
 * programs**, never recurring classes.
 *
 * `getEventsForOrg` narrows these structurally before `take`: standalone
 * events have no grouping/parent, while program sessions belong to a
 * program-typed grouping. Native commerce discovery replaces those sessions
 * with one whole-program card when available.
 */
export const EVENTS_EMBED_TYPE_FILTER: readonly string[] = ["event", "program"];

/**
 * True for a recurring-class occurrence, which the events embed must never
 * list. Two shapes exist in the data model:
 *   - grouped classes: `eventGroupingId` set, `eventGroupingType === 'class'`
 *   - legacy recurring classes: `parentEventId` set, no event-grouping
 *
 * Programs are grouped too (`eventGroupingId` set), but carry
 * `eventGroupingType === 'program'`, so they are deliberately *not* excluded.
 *
 * This is a safety net, not the primary filter. Broadening the request to
 * `type: ['all']` and relying on this alone does not work: the API applies
 * `take` before any client sees the rows, and recurring occurrences outnumber
 * events/programs by roughly two orders of magnitude, so the take window fills
 * with classes and starves out the rows the widget actually wants. The narrow
 * request stays the mechanism; this only guarantees classes can never leak
 * back in if the server-side filter is ever widened.
 */
export function isRecurringClass(ev: {
  eventGroupingType?: ApiEventGroupingType | null;
  parentEventId?: number | null;
}): boolean {
  return ev.eventGroupingType === "class" || ev.parentEventId != null;
}

export async function fetchEvents(orgId: number): Promise<MockEvent[]> {
  const { minDate } = isoRange(0, 7);
  const pageSize = 100;
  // E2: the `type` filter narrows this at the API rather than client-side
  // (event.service.ts filters on eventGroupingId/parentEventId — recurring
  // class occurrences carry one of those, standalone events carry neither).
  // Previously requested `['event', 'class']`, which also pulled in every
  // recurring class occurrence (e.g. "Reformer 1", "Yin Yoga") with no way to
  // filter them back out client-side.
  const [events, programs, filters] = await Promise.all([
    (async (): Promise<EmbeddedEventDto[]> => {
      const all: EmbeddedEventDto[] = [];
      for (let skip = 0; ; skip += pageSize) {
        const page = (await getEmbeddedApiClients(orgId).events.getEventsNoAuth(
          {
            getEventsNoAuthDto: {
              orgIds: [orgId],
              minDate,
              // The API treats an empty bound as open-ended. Keep each response
              // bounded with take/skip instead of silently dropping events after
              // an arbitrary future date or requesting one enormous payload.
              maxDate: "",
              take: pageSize,
              skip,
              type: [...EVENTS_EMBED_TYPE_FILTER],
              includePaymentOptions: true,
            },
          },
        )) as EmbeddedEventDto[];
        all.push(...page);
        if (page.length < pageSize) return all;
      }
    })(),
    discoverCommercePrograms(orgId).catch(() => []),
    fetchFilterData(orgId).catch(
      (): EventFilterDataDto | undefined => undefined,
    ),
  ]);
  const providerName = nameMap(filters?.providerUsers);
  const hasProgramDiscovery = programs.length > 0;
  const out: MockEvent[] = [];
  for (const ev of events) {
    // Belt-and-braces: recurring classes are already excluded by the request,
    // but never render one even if the server-side filter widens.
    if (
      isRecurringClass(ev) ||
      (hasProgramDiscovery && ev.eventGroupingType === "program")
    ) {
      continue;
    }
    const act = ev.activities?.[0];
    const startIso = ev.details?.start || act?.start;
    if (!startIso) continue;
    const start = new Date(startIso);
    const endIso = ev.details?.end || act?.end;
    // PLAT-6994: an event priced only through membership tiers / credit packs
    // has no default drop-in, so `payment` is absent and the price reads as 0.
    // The server already says whether that 0 really means free for a guest;
    // trust it rather than inferring "Free" from a missing drop-in.
    const requiresEntitlement =
      !act?.externalRegistration?.enabled &&
      !!act?.paymentOptions &&
      !act.paymentOptions.payment &&
      act.paymentOptions.isPubliclyFree === false;
    out.push({
      id: ev.slug || String(ev.id),
      title: ev.name,
      type: "Event",
      instructor: (act?.providerUserIds ?? [])
        .map((providerId) => providerName[providerId])
        .filter(Boolean)
        .join(", "),
      month: start.toLocaleString("en-US", { month: "short" }),
      day: start.getDate(),
      weekday: start.toLocaleString("en-US", { weekday: "long" }),
      year: start.getFullYear(),
      start: fmtTime(startIso, act?.timezone),
      end: fmtTime(endIso, act?.timezone),
      location: ev.details?.location?.name || "",
      address: "",
      timezone: act?.timezone || "",
      price: act?.externalRegistration?.enabled
        ? act.externalRegistration.advertisedPrice ?? 0
        : act?.paymentOptions?.payment?.price ?? 0,
      ...(requiresEntitlement
        ? {
            requiresEntitlement: true,
            tiers: tiersFromPaymentOptions(act.paymentOptions ?? undefined),
          }
        : {}),
      ...(act?.externalRegistration?.enabled
        ? {
            externalTickets: true,
            externalTicketUrl: getSafeExternalTicketUrl(
              act.externalRegistration.url,
            ),
            advertisedTicketPrice:
              act.externalRegistration.advertisedPrice ?? undefined,
          }
        : {}),
      image: ev.details?.headerImageUrl || "",
      summary: stripHtml(ev.details?.contentHtml),
      description: toDescriptionText(ev.details?.contentHtml),
      stripeProductId: act?.stripeProductId ?? undefined,
      activityId: act?.id,
      // ST1 pre-filter ids: event-grouping (settings' event filter), host
      // provider user ids (host filter), and location id (location filter).
      eventGroupingId: ev.eventGroupingId ?? undefined,
      providerUserIds: act?.providerUserIds ?? [],
      locationId: ev.locationId ?? undefined,
      /** ISO start for sorting (soonest first). */
      startIso,
      endIso: endIso || undefined,
      openRegistrationTimeFrame: act?.openRegistrationTimeFrame === true,
      registrationClosesAt: act?.registrationClosesAt,
      // E3: forms (waiver/intake) attached to this event's activity — the
      // events embed uses this to fetch + render the real waiver form content
      // instead of hardcoded placeholder copy (mirrors fetchSchedule's
      // `classes[].forms`).
      forms: (act?.forms ?? [])
        .filter((f) => !!f.formId)
        .map((f) => ({
          formId: f.formId,
          type: f.type || "other",
          requirement: f.requirement,
        })),
    });
  }
  for (const program of programs) {
    const first = program.sessions[0];
    const last = program.sessions[program.sessions.length - 1];
    if (!first || !last) continue;
    const start = new Date(first.startsAt);
    out.push({
      id: `program:${program.programKey}`,
      title: program.name,
      type: "Program",
      instructor: [
        first.providerName,
        first.secondaryProviderName,
        first.tertiaryProviderName,
      ]
        .filter(Boolean)
        .join(", "),
      month: start.toLocaleString("en-US", { month: "short" }),
      day: start.getDate(),
      weekday: start.toLocaleString("en-US", { weekday: "long" }),
      year: start.getFullYear(),
      start: fmtTime(first.startsAt),
      end: fmtTime(last.endsAt),
      location: first.locationName ?? "",
      address: "",
      timezone: "",
      price: program.priceMinor / 100,
      image: program.imageUrl ?? "",
      summary: program.description ?? "",
      description: program.description ?? "",
      eventGroupingId: program.programKey,
      providerUserIds: [
        ...new Set(
          program.sessions.flatMap((session) =>
            [
              session.providerId,
              session.secondaryProviderId,
              session.tertiaryProviderId,
            ].filter((id): id is number => typeof id === "number"),
          ),
        ),
      ],
      locationId: first.locationId ?? undefined,
      startIso: first.startsAt,
      endIso: last.endsAt,
      program: {
        programKey: program.programKey,
        sessionKeys: program.sessions.map(({ sessionKey }) => sessionKey),
        sessions: program.sessions.map((session) => ({
          sessionKey: session.sessionKey,
          label: session.label,
          startsAt: session.startsAt,
          endsAt: session.endsAt,
          ...([
            session.providerName,
            session.secondaryProviderName,
            session.tertiaryProviderName,
          ]
            .filter(Boolean)
            .join(", ")
            ? {
                providerName: [
                  session.providerName,
                  session.secondaryProviderName,
                  session.tertiaryProviderName,
                ]
                  .filter(Boolean)
                  .join(", "),
              }
            : {}),
          ...(session.locationName
            ? { locationName: session.locationName }
            : {}),
        })),
      },
    });
  }
  out.sort((a, b) => {
    const ta = a.startIso ? new Date(a.startIso).getTime() : 0;
    const tb = b.startIso ? new Date(b.startIso).getTime() : 0;
    return ta - tb;
  });
  return out;
}

type CompatibleEmbedProductDto = Omit<EmbedProductDto, "category"> & {
  category: EmbedProductDto["category"] | "specials";
  /** Accepted from older embedded-product payloads; current OpenAPI omits it. */
  features?: string[];
};

/** SHOP-ORD: one provider-configured subcategory section for the shop embed. */
export interface ShopSection {
  id: string;
  title: string;
  items: MockProduct[];
}

export interface ShopProducts {
  specials: MockProduct[];
  memberships: MockProduct[];
  packs: MockProduct[];
  giftCards: MockProduct[];
  /**
   * SHOP-ORD: the org's products grouped into the provider's Manage-Shop-View
   * subcategories, in the configured display order (uncategorized products go
   * into a trailing "Products" section). Empty when the org hasn't set up any
   * subcategories — the embed then falls back to the type-based sections above.
   */
  sections: ShopSection[];
}

/** Fetch the org's purchasable products for the shop embed, grouped by category. */
export async function fetchProducts(
  orgId: number,
  signal?: AbortSignal,
): Promise<ShopProducts> {
  const list = (await getEmbeddedApiClients(
    orgId,
  ).publicEmbeds.getEmbedProducts(
    { orgId },
    signal === undefined ? undefined : { signal },
  )) as CompatibleEmbedProductDto[];
  const toProduct = (p: CompatibleEmbedProductDto): MockProduct => ({
    id: p.id,
    name: p.name,
    price: p.price,
    cadence: p.cadence,
    description: p.description || "",
    features: p.features ?? [],
    stripeProductId: p.stripeProductId ?? undefined,
    deferredChargeSettings: p.deferredChargeSettings ?? undefined,
    // Carried through so the shop can tell a gift card apart from an ordinary
    // product even when it renders inside a provider subcategory (where the
    // type-based buckets below are unused). Gift cards check out through
    // /gift-cards/purchase-intent, not the plain product charge.
    category: p.category,
    // SHOP-42: the product's OWN callout badge text (undefined → no banner).
    callout: p.callout || undefined,
    firstTimeClientsOnly: !!p.firstTimeClientsOnly,
    // F3: pass through the assigned terms/waiver form id, if the API sends one.
    formId: p.formId || undefined,
  });
  // SHOP-41: `list` arrives pre-sorted by the API (ordinal asc, nulls last);
  // filtering into category buckets preserves that server order — do NOT
  // re-sort here.
  const group = (cat: CompatibleEmbedProductDto["category"]) =>
    list.filter((p) => p.category === cat).map(toProduct);

  // SHOP-ORD: group products into the provider's subcategories (in order). The
  // API already returns the list sorted by (subcategory order, position within
  // subcategory), so preserving insertion order per group is correct.
  const bySub = new Map<
    string,
    { title: string; ordinal: number; items: MockProduct[] }
  >();
  const uncategorized: MockProduct[] = [];
  for (const p of list) {
    if (p.subcategoryId && p.subcategoryName) {
      const g = bySub.get(p.subcategoryId) ?? {
        title: p.subcategoryName,
        ordinal: p.subcategoryOrdinal ?? 0,
        items: [],
      };
      g.items.push(toProduct(p));
      bySub.set(p.subcategoryId, g);
    } else {
      uncategorized.push(toProduct(p));
    }
  }
  // Only follow subcategories when the provider actually configured at least
  // one; otherwise leave `sections` empty so the embed keeps the type-based
  // sections (and the `show` toggle) for orgs that never set up subcategories.
  const sections: ShopSection[] =
    bySub.size > 0
      ? [
          ...[...bySub.entries()]
            .sort((a, b) => a[1].ordinal - b[1].ordinal)
            .map(([id, g]) => ({ id, title: g.title, items: g.items })),
          ...(uncategorized.length
            ? [{ id: "uncategorized", title: "Products", items: uncategorized }]
            : []),
        ]
      : [];

  return {
    specials: group("specials"),
    memberships: group("memberships"),
    packs: group("packs"),
    giftCards: group("giftCards"),
    sections,
  };
}

export interface CatalogEntry {
  name: string;
  price: number;
  stripeProductId?: string;
  /** Authoritative product classification used by booking payment labels. */
  category?: MockProduct["category"];
  /** Human/API cadence such as `one-time` or `per month`. */
  cadence?: MockProduct["cadence"];
  /**
   * The product's `prior`-trigger form (`packageForms`), surfaced by the API as
   * `EmbedProductDto.formId`.
   *
   * This is a SEPARATE requirement from the class/appointment's own forms, and
   * it is the one the charge enforces: `RosterChargeService.validateRequiredForms`
   * rejects the purchase unless the buyer has a response for exactly this form.
   * Booking checkout previously read only the activity's forms, so a customer
   * could complete every form they were shown and still be told "This product
   * requires a form to be completed before purchase."
   */
  priorFormId?: number;
}

/**
 * Product catalog lookup, keyed by BOTH the Zipper product id and the Stripe
 * product id → {name, price, stripeProductId}. Booking pay-options resolve
 * membership/pack tiers against this to get the real name + price (mirrors the
 * customer app's `org.getOrgProductById(productId)`), since the schedule feed's
 * payment options carry a Zipper product id + a placeholder price/label.
 * Retries a failed or stalled read once, then returns {} for callers' safe
 * fallback.
 */
export async function fetchProductCatalog(
  orgId: number,
): Promise<Record<string, CatalogEntry>> {
  for (let attempt = 1; attempt <= 2; attempt++) {
    try {
      const p = await fetchProductsWithin(orgId, CATALOG_ATTEMPT_TIMEOUT_MS);
      const map: Record<string, CatalogEntry> = {};
      for (const prod of [
        ...p.specials,
        ...p.memberships,
        ...p.packs,
        ...p.giftCards,
      ]) {
        const entry: CatalogEntry = {
          name: prod.name,
          price: prod.price,
          stripeProductId: prod.stripeProductId,
          category: prod.category,
          cadence: prod.cadence,
          priorFormId: prod.formId,
        };
        if (prod.id) map[prod.id] = entry;
        if (prod.stripeProductId) map[prod.stripeProductId] = entry;
      }
      return map;
    } catch (error) {
      // Transport objects can contain auth headers; log only safe diagnostics.
      console.warn("[Zipper] Product catalog request failed", {
        orgId,
        attempt,
        error: error instanceof Error ? error.name : "UnknownError",
      });
    }
  }
  return {};
}

/** Per-attempt cap: checkout shows "Loading payment options…" until the
 * catalog settles, so a request that never answers must count as failed. */
const CATALOG_ATTEMPT_TIMEOUT_MS = 8_000;

/**
 * `fetchProducts` bounded by `timeoutMs`. Aborts the request and rejects with
 * a `TimeoutError` even if the transport ignores the abort signal.
 */
async function fetchProductsWithin(
  orgId: number,
  timeoutMs: number,
): Promise<ShopProducts> {
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timedOut = new Promise<never>((_, reject) => {
    timer = globalThis.setTimeout(() => {
      const reason = new DOMException("Timed out", "TimeoutError");
      controller.abort(reason);
      reject(reason);
    }, timeoutMs);
  });
  try {
    return await Promise.race([fetchProducts(orgId, controller.signal), timedOut]);
  } finally {
    globalThis.clearTimeout(timer);
  }
}

/** Real room layout + spot availability for a class instance (the seat map). */
export function fetchSpotAvailability(
  orgId: number,
  roomId: string,
  startsAt: string,
  endsAt: string,
): Promise<RoomAvailability> {
  return getEmbeddedApiClients(orgId).publicEmbeds.getRoomAvailability({
    orgId,
    roomId,
    startsAt,
    endsAt,
  });
}

/** Hold a spot for the checkout window. Throws on 409 (already taken). */
export function createSpotHold(
  orgId: number,
  params: {
    spotId: string;
    startsAt: string;
    endsAt: string;
    holderKey: string;
  },
): Promise<{ holdId: string; expiresAt: string }> {
  const api = getEmbeddedApiClients(orgId);
  // Signed-in registration validates the hold against the authenticated user.
  // Let the same authenticated API derive that owner instead of storing the
  // guest checkout token, which cannot match the registration's user ID.
  if (authHeaders(orgId).Authorization) {
    const { spotId, startsAt, endsAt } = params;
    return api.spotHolds.createHold({
      orgId,
      createSpotHoldDto: { spotId, startsAt, endsAt },
    });
  }
  return api.publicEmbeds.createSpotHold({
    orgId,
    embedSpotHoldRequestDto: params,
  });
}

/** Release a previously held spot (reselect / abandon). Best-effort. */
export async function releaseSpotHold(
  orgId: number,
  holdId: string,
  holderKey: string,
): Promise<void> {
  try {
    const api = getEmbeddedApiClients(orgId);
    if (authHeaders(orgId).Authorization) {
      await api.spotHolds.releaseHold({ orgId, id: holdId });
      return;
    }
    await api.publicEmbeds.releaseSpotHold({
      orgId,
      embedReleaseSpotHoldRequestDto: { holdId, holderKey },
    });
  } catch {
    /* best-effort; holds expire anyway */
  }
}

export function fetchMonthIndicators(
  orgId: number,
  year: number,
  month: number,
  locationId?: string,
): Promise<MonthIndicators> {
  return getEmbeddedApiClients(orgId).schedule.getMonthIndicators({
    orgId,
    year,
    month,
    locationIds: locationId,
  });
}

/**
 * PLAT-6790: instructor/location names change rarely, but this response is
 * heavy (the org's events for ±6 months, groupings and users). Every schedule
 * window used to re-download it, so keep one per org for a few minutes; a
 * failure is not cached.
 */
const FILTER_DATA_TTL_MS = 5 * 60 * 1000;
const filterDataCache = new Map<
  number,
  { at: number; data: Promise<EventFilterDataDto> }
>();

export function fetchFilterData(orgId: number): Promise<EventFilterDataDto> {
  const hit = filterDataCache.get(orgId);
  if (hit && Date.now() - hit.at < FILTER_DATA_TTL_MS) return hit.data;
  // `scope=people`: only providerUsers + locations — all the embeds read.
  const data = getEmbeddedApiClients(orgId)
    .events.getEventsFilterData({ orgId, scope: "people" })
    .catch((error: unknown) => {
      if (filterDataCache.get(orgId)?.data === data) {
        filterDataCache.delete(orgId);
      }
      throw error;
    });
  filterDataCache.set(orgId, { at: Date.now(), data });
  return data;
}

/** Test seam: forget cached filter data. */
export function resetFilterDataCache(): void {
  filterDataCache.clear();
}

export type PromoResult = ValidatePromotionCodeResponseDto;

/**
 * Validate a promo/discount code against the org's Stripe coupons. Public,
 * anon endpoint — returns 200 with `valid:false` + errorMessage for bad codes.
 */
export function validatePromotionCode(
  orgId: number,
  body: Pick<
    ValidatePromotionCodeRequestDto,
    "code" | "stripeProductId" | "customerEmail" | "orderAmountInCents"
  >,
): Promise<PromoResult> {
  return getEmbeddedApiClients(orgId).payments.validatePromotionCode({
    orgId,
    validatePromotionCodeRequestDto: body,
  });
}

/** Resolve a validated promo result to a dollar discount against a subtotal. */
export function promoDiscount(r: PromoResult, subtotal: number): number {
  if (!r.valid || !r.discountAmount) return 0;
  const d =
    r.discountType === "percentage"
      ? (subtotal * r.discountAmount) / 100
      : r.discountAmount / 100;
  return Math.min(subtotal, Math.round(d * 100) / 100);
}

type EmbeddedProviderUser = EventFilterDataDto["providerUsers"][number] & {
  /** Compatibility with older filter payloads that included a display name. */
  name?: string;
};

function nameMap(
  users?: ReadonlyArray<EmbeddedProviderUser> | null,
): Record<number, string> {
  const m: Record<number, string> = {};
  for (const u of users ?? []) {
    m[u.id] = u.name || `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim();
  }
  return m;
}

/** provider user id → headshot URL (from the events-filter-data payload). */
function photoMap(
  users?: ReadonlyArray<EmbeddedProviderUser> | null,
): Record<number, string> {
  const m: Record<number, string> = {};
  for (const u of users ?? []) {
    const url = u.attributes?.profileAvatarImageUrl;
    if (url) m[u.id] = url;
  }
  return m;
}

/** provider user id → sanitized bio HTML (from the events-filter-data payload). */
function bioMap(
  users?: ReadonlyArray<EmbeddedProviderUser> | null,
): Record<number, string> {
  const m: Record<number, string> = {};
  for (const u of users ?? []) {
    if (u.biography) m[u.id] = u.biography;
  }
  return m;
}

/**
 * Derive the reference capacity vocabulary ("{n} left" / "{n} waiting") from an
 * activity's registration cap + current registrations — both present on the
 * public events-no-auth payload (`allowedRegistrationQuantity` + `registrations`
 * with a `status`). Returns `spotsLeft: -1` when the activity has no cap
 * (unlimited), which the component reads as "unknown" and hides the line.
 */
type CapacityActivity = Partial<
  Pick<
    EventActivityDto,
    | "id"
    | "allowedRegistrationQuantity"
    | "allowWaitlist"
    | "activeRegistrationCount"
    | "waitlistRegistrationCount"
  >
> & {
  registrations?: ReadonlyArray<{ status?: string | null }> | null;
  /** A private-session buyout owns every seat: full, no waitlist. */
  privateSessionBooked?: boolean;
};

export function deriveCapacity(act: CapacityActivity): {
  capacityState: CapacityState;
  spotsLeft: number;
  spotsTotal?: number;
} {
  const max = act.allowedRegistrationQuantity;
  if (typeof max !== "number" || max <= 0) {
    return { capacityState: "open", spotsLeft: -1 };
  }
  if (act.privateSessionBooked) {
    return { capacityState: "almost-full", spotsLeft: 0, spotsTotal: max };
  }
  const regs = act.registrations ?? [];
  const active =
    typeof act.activeRegistrationCount === "number"
      ? act.activeRegistrationCount
      : regs.filter((r) => r.status === "active").length;
  const waiting =
    typeof act.waitlistRegistrationCount === "number"
      ? act.waitlistRegistrationCount
      : regs.filter((r) => r.status === "waitlist").length;
  const available = max - active;
  if (available > 0) {
    const state: CapacityState =
      available <= 3 ? "almost-full" : available <= 6 ? "filling" : "open";
    return { capacityState: state, spotsLeft: available, spotsTotal: max };
  }
  // Full: surface the waitlist count when the activity takes a waitlist,
  // otherwise "0 left".
  if (act.allowWaitlist) {
    return { capacityState: "waitlist", spotsLeft: waiting, spotsTotal: max };
  }
  return { capacityState: "almost-full", spotsLeft: 0, spotsTotal: max };
}

export function locationMap(
  locs?: ReadonlyArray<Partial<EventFilterDataDto["locations"][number]>> | null,
): Record<string, string> {
  const m: Record<string, string> = {};
  for (const l of locs ?? []) {
    if (l.id) m[l.id] = l.name || "";
  }
  return m;
}

/** Map locationId -> full formatted address (for the Maps link-out), so the
 * schedule links to the actual studio address rather than just searching the
 * location's short name (e.g. "Wyckoff"). */
export function locationAddressMap(
  locs?: ReadonlyArray<Partial<EventFilterDataDto["locations"][number]>> | null,
): Record<string, string> {
  const m: Record<string, string> = {};
  for (const l of locs ?? []) {
    const addr = l.attributes?.place?.formattedAddress;
    if (l.id && addr) m[l.id] = addr;
  }
  return m;
}

/** Build booking tiers (with real Stripe product ids) from an activity's payment options. */
export function tiersFromPaymentOptions(po?: ApiPaymentOptions): PricingTier[] {
  if (!po) return [];
  const tiers: PricingTier[] = [];
  if (po.payment) {
    tiers.push({
      id: "dropin",
      label: "Drop-in",
      sub: po.payment.priceBeforeDiscount != null ? "Member price" : "This class only",
      price: po.payment.price,
      perSession: po.payment.price,
      sessions: 1,
      recurring: false,
      stripeProductId: po.dropInStripeProductId || po.payment.productId,
    });
  }
  // D2: `relevantToTargetEvent === false` means this credit pack doesn't
  // apply to the service/class type being booked at all — it must not be
  // offered as a purchase option here, not just relabeled.
  // PLAT-6856: one dedup set across both buckets — the same product can come
  // back as a punch card and again in subscriptionPrices.
  const seenProductIds = new Set<string>();
  for (const pc of po.punchCards ?? []) {
    if (pc.relevantToTargetEvent === false) continue;
    // PLAT-6856: a placeholder (no card held) carries no name or price and is
    // not filtered for hidden/deleted products. Purchasable products already
    // arrive via subscriptionPrices; owned credits have a real punchCard.
    if (!pc.punchCard) continue;
    if (seenProductIds.has(pc.productId)) continue;
    seenProductIds.add(pc.productId);
    const name = pc.punchCard?.product?.name || pc.punchCard?.name;
    const price = pc.punchCard?.product?.price ?? pc.punchCard?.price ?? 0;
    // PLAT-5624: an unlimited membership carries no pre-issued credits, so its
    // numberOfUsesRemaining is 0 — label it "Unlimited credits" rather than
    // falling through to the generic "Class pack" copy.
    const isUnlimited = Boolean(
      pc.isUnlimited || pc.punchCard?.group?.isUnlimited,
    );
    tiers.push({
      id: pc.productId,
      deferredChargeSettings: po.subscriptionPrices?.find((s) => s.productId === pc.productId)?.deferredChargeSettings ?? undefined,
      deferredMembershipPurchaseId: po.subscriptionPrices?.find((s) => s.productId === pc.productId)?.deferredMembershipPurchaseId ?? undefined,
      label: name || "Class pack",
      sub: isUnlimited
        ? "Unlimited credits"
        : pc.numberOfUsesRemaining
          ? `${pc.numberOfUsesRemaining} credit${pc.numberOfUsesRemaining === 1 ? "" : "s"} remaining`
          : "Class pack",
      price,
      perSession: null,
      sessions: isUnlimited ? null : (pc.numberOfUsesRemaining ?? null),
      recurring: false,
      stripeProductId: pc.productId,
    });
  }
  // D2: `EventPaymentOptionDto` carries no name at all — real membership
  // names are resolved after the fact by `enrichTierNames()` against the
  // product catalog. Dedup defensively in case the same product comes back
  // more than once (observed as e.g. three identical "Membership $100" rows).
  for (const sub of po.subscriptionPrices ?? []) {
    if (seenProductIds.has(sub.productId)) continue;
    seenProductIds.add(sub.productId);
    tiers.push({
      id: sub.productId,
      deferredChargeSettings: sub.deferredChargeSettings ?? undefined,
      deferredMembershipPurchaseId: sub.deferredMembershipPurchaseId ?? undefined,
      isDeferredMembershipAccess: sub.isDeferredMembershipAccess ?? undefined,
      label: "Membership",
      sub: "Recurring",
      price: sub.price,
      perSession: null,
      sessions: null,
      recurring: true,
      stripeProductId: sub.productId,
    });
  }
  return tiers;
}
